import { HDKey } from '@scure/bip32';
import { type Address, type Network, MAINNET, p2wpkh } from './address';
import { equal, hex } from './bytes';
import { seedFromMnemonic } from './mnemonic';
import { Transaction, input } from './tx';
import type { SendPlan } from './txbuilder';

export const RECEIVE_CHAIN = 0;
export const CHANGE_CHAIN = 1;

/**
 * A single-account BIP84 (native segwit) wallet derived from a recovery phrase.
 * Paths: m/84'/coin'/0'/0/i (receive) and m/84'/coin'/0'/1/i (change), with
 * coin = 0 on COINWOW mainnet, as in COINWOW Core and the iOS app.
 */
export class HDWallet {
  private cache = new Map<string, HDKey>();

  private constructor(
    readonly network: Network,
    private readonly account: HDKey,
    readonly masterFingerprint: number,
  ) {}

  static async fromMnemonic(phrase: string, passphrase = '', network: Network = MAINNET): Promise<HDWallet> {
    const seed = await seedFromMnemonic(phrase, passphrase);
    return HDWallet.fromSeed(seed, network);
  }

  static fromSeed(seed: Uint8Array, network: Network = MAINNET): HDWallet {
    const master = HDKey.fromMasterSeed(seed, network.bip32);
    seed.fill(0);
    const account = master.derive(`m/84'/${network.coinType}'/0'`);
    return new HDWallet(network, account, master.fingerprint);
  }

  get accountPath(): string {
    return `m/84'/${this.network.coinType}'/0'`;
  }

  /** Account extended public key (watch-only export). */
  get accountXpub(): string {
    return this.account.publicExtendedKey;
  }

  private key(chain: number, index: number): HDKey {
    const id = `${chain}/${index}`;
    let k = this.cache.get(id);
    if (!k) {
      k = this.account.deriveChild(chain).deriveChild(index);
      this.cache.set(id, k);
    }
    return k;
  }

  publicKey(chain: number, index: number): Uint8Array {
    return this.key(chain, index).publicKey!;
  }

  address(chain: number, index: number): Address {
    return p2wpkh(this.publicKey(chain, index), this.network);
  }

  receiveAddress(index: number): Address {
    return this.address(RECEIVE_CHAIN, index);
  }

  changeAddress(index: number): Address {
    return this.address(CHANGE_CHAIN, index);
  }

  /** Output descriptor for COINWOW Core (`importdescriptors`), public keys only. */
  descriptor(chain: number): string {
    const fp = hex.encode(Uint8Array.of(
      (this.masterFingerprint >>> 24) & 0xff, (this.masterFingerprint >>> 16) & 0xff,
      (this.masterFingerprint >>> 8) & 0xff, this.masterFingerprint & 0xff));
    const body = `wpkh([${fp}/84h/${this.network.coinType}h/0h]${this.accountXpub}/${chain}/*)`;
    return `${body}#${descriptorChecksum(body)}`;
  }

  /**
   * Private descriptor to restore this wallet in COINWOW Core without our
   * servers (account xprv). Sensitive: show only after authentication.
   */
  privateDescriptor(chain: number): string {
    const fp = hex.encode(Uint8Array.of(
      (this.masterFingerprint >>> 24) & 0xff, (this.masterFingerprint >>> 16) & 0xff,
      (this.masterFingerprint >>> 8) & 0xff, this.masterFingerprint & 0xff));
    const body = `wpkh([${fp}/84h/${this.network.coinType}h/0h]${this.account.privateExtendedKey}/${chain}/*)`;
    return `${body}#${descriptorChecksum(body)}`;
  }

  /** Builds and signs the transaction described by `plan`. */
  sign(plan: SendPlan): Transaction {
    const outputs = [{ value: plan.amount, scriptPubKey: plan.recipient.scriptPubKey }];
    if (plan.change > 0 && plan.changeIndex !== null) {
      outputs.push({ value: plan.change, scriptPubKey: this.changeAddress(plan.changeIndex).scriptPubKey });
    }
    const tx = new Transaction(plan.coins.map((c) => input(c.outpoint)), outputs);
    plan.coins.forEach((coin, i) => {
      const k = this.key(coin.chain, coin.index);
      // Never sign for a script this key does not control.
      if (!k.privateKey || !equal(p2wpkh(k.publicKey!, this.network).scriptPubKey, coin.scriptPubKey)) {
        throw new Error('coin does not belong to this wallet');
      }
      tx.signP2WPKH(i, k.privateKey, coin.value);
    });
    return tx;
  }

  /** Forget cached private keys (best effort; JavaScript cannot guarantee erasure). */
  wipe() {
    for (const k of this.cache.values()) k.wipePrivateData();
    this.cache.clear();
    this.account.wipePrivateData();
  }
}

// Output descriptor checksum (BIP380), as computed by COINWOW Core.
const INPUT_CHARSET = "0123456789()[],'/*abcdefgh@:$%{}IJKLMNOPQRSTUVWXYZ&+-.;<=>?!^_|~ijklmnopqrstuvwxyzABCDEFGH`#\"\\ ";
const CHECKSUM_CHARSET = 'qpzry9x8gf2tvdw0s3jn54khce6mua7l';

function polymod(c: bigint, val: number): bigint {
  const c0 = Number(c >> 35n);
  c = ((c & 0x7ffffffffn) << 5n) ^ BigInt(val);
  if (c0 & 1) c ^= 0xf5dee51989n;
  if (c0 & 2) c ^= 0xa9fdca3312n;
  if (c0 & 4) c ^= 0x1bab10e32dn;
  if (c0 & 8) c ^= 0x3706b1677an;
  if (c0 & 16) c ^= 0x644d626ffdn;
  return c;
}

export function descriptorChecksum(desc: string): string {
  let c = 1n;
  let cls = 0;
  let clscount = 0;
  for (const ch of desc) {
    const pos = INPUT_CHARSET.indexOf(ch);
    if (pos < 0) return '';
    c = polymod(c, pos & 31);
    cls = cls * 3 + (pos >> 5);
    if (++clscount === 3) {
      c = polymod(c, cls);
      cls = 0;
      clscount = 0;
    }
  }
  if (clscount > 0) c = polymod(c, cls);
  for (let j = 0; j < 8; j++) c = polymod(c, 0);
  c ^= 1n;
  let out = '';
  for (let j = 0; j < 8; j++) out += CHECKSUM_CHARSET[Number((c >> BigInt(5 * (7 - j))) & 31n)];
  return out;
}
